import { useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";
import { Star, Clock, Check, X, AlertTriangle, Loader2 } from "lucide-react";

export default function MyContributions() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, rejected: 0 });

  const fetchContributions = useCallback(async () => {
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("access_token");
      if (!token) { setLoading(false); return; }
      const res = await axios.get(`${API}/birthday-offers?page=1&limit=50`, { headers: { Authorization: `Bearer ${token}` } });
      const all = (res.data.offers || []).filter(o => o.submitted_by === "user");
      setOffers(all);
      setStats({
        total: all.length,
        approved: all.filter(o => o.user_submission_status === "approved").length,
        pending: all.filter(o => o.user_submission_status === "pending").length,
        rejected: all.filter(o => o.user_submission_status === "rejected").length,
      });
    } catch { setOffers([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchContributions(); }, [fetchContributions]);

  if (loading) return <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-amber-500" /></div>;
  if (offers.length === 0) return null;

  const statusConfig = {
    approved: { icon: Check, color: "bg-emerald-100 text-emerald-700", label: "Approved" },
    pending: { icon: Clock, color: "bg-yellow-100 text-yellow-700", label: "Pending" },
    needs_review: { icon: AlertTriangle, color: "bg-amber-100 text-amber-700", label: "Needs Review" },
    rejected: { icon: X, color: "bg-slate-100 text-slate-500", label: "Rejected" },
  };

  return (
    <div className="space-y-3" data-testid="my-contributions-section">
      <h3 className="text-sm font-bold text-slate-700">My Contributions</h3>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2 text-center">
        <div className="bg-slate-50 rounded-lg p-2">
          <p className="text-lg font-black text-slate-700">{stats.total}</p>
          <p className="text-[9px] text-slate-500 uppercase">Total</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-2">
          <p className="text-lg font-black text-emerald-600">{stats.approved}</p>
          <p className="text-[9px] text-emerald-500 uppercase">Approved</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-2">
          <p className="text-lg font-black text-yellow-600">{stats.pending}</p>
          <p className="text-[9px] text-yellow-500 uppercase">Pending</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2">
          <p className="text-lg font-black text-slate-400">{stats.rejected}</p>
          <p className="text-[9px] text-slate-400 uppercase">Rejected</p>
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {offers.slice(0, 10).map(o => {
          const sc = statusConfig[o.user_submission_status] || statusConfig.pending;
          const Icon = sc.icon;
          return (
            <div key={o.offer_id} className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3" data-testid={`contribution-${o.offer_id}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center ${sc.color}`}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{o.offer_title}</p>
                <p className="text-[10px] text-slate-400">{o.offer_type?.replace(/_/g, " ")} — {o.created_at?.slice(0, 10)}</p>
                {o.admin_review_notes && <p className="text-[10px] text-slate-500 mt-0.5 italic">{o.admin_review_notes}</p>}
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${sc.color}`}>{sc.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
